import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { ContextService } from '@maximizer/core/shared/data-access';
import { ListItem, Octopus } from '@maximizer/core/shared/domain';
import {
  bufferCount,
  catchError,
  concatMap,
  from,
  map,
  Observable,
  of,
  switchMap,
  tap,
  throwError,
  toArray,
} from 'rxjs';
import {
  AppointmentFieldOption,
  AppointmentForm,
  AppointmentRecurrence,
  AppointmentSummary,
  AppointmentFieldOptionMapper,
  AppointmentFormMapper,
  AppointmentSummaryMapper,
  CalendarAttendee,
  OutlookCalendarEvent,
  OutlookCalendarEventSummary,
  SeriesSaveProgress,
  SeriesSaveProgressCallback,
  mapCalendarEventToAppointmentCreate,
  mapCalendarEventToAppointmentDates,
  mapRecurrenceToRecurringPattern,
} from '@maximizer/outlook/shared/domain';
import { skipTimeoutRetry } from '../interceptor/retry-timeout.interceptor';
import { resolveAppointmentLocation } from '../utils/appointment.util';
import { toUtcInstant } from '../utils/client-time.util';
import {
  MAX_RECURRENCE_OCCURRENCES,
  expandRecurrenceDates,
  shiftAppointmentFormToDay,
  shiftCalendarEventToDay,
} from '../utils/recurrence.util';

export class AppointmentSeriesError extends Error {
  constructor(
    readonly appointmentKey: string,
    readonly progress: SeriesSaveProgress,
    cause: unknown,
  ) {
    super(
      `Recurring appointment saved ${progress.savedCount} of ${progress.totalCount} occurrences`,
      { cause },
    );
    this.name = 'AppointmentSeriesError';
  }
}

export class AppointmentOccurrenceLimitError extends Error {
  constructor(
    readonly occurrenceCount: number,
    readonly maxOccurrences: number,
  ) {
    super(
      `Recurring appointment has ${occurrenceCount} occurrences; the limit is ${maxOccurrences}`,
    );
    this.name = 'AppointmentOccurrenceLimitError';
  }
}

const APPOINTMENT_SCHEMA_COMPATIBILITY = {
  SchemaObject: '1.0',
  RecurringPatternObject: '1.0',
};

const FIRST_BATCH_INDEX = 1;
const OCTOPUS_BATCH_SIZE = 20;

function batchItemKey(index: number): `Appointment.${number}` {
  return `Appointment.${index + FIRST_BATCH_INDEX}`;
}

@Injectable()
export class AppointmentService {
  constructor(
    private readonly http: HttpClient,
    private readonly context: ContextService,
  ) {}

  getFieldOption(): Observable<AppointmentFieldOption> {
    const request: Octopus.AppointmentFieldOptionsRequest = {
      Appointment: {
        FieldOptions: {
          InteractionCategory: [{ Key: 1, DisplayValue: 1 }],
          Result: [{ Key: 1, DisplayValue: 1 }],
          Location: [{ Key: 1, DisplayValue: 1 }],
        },
        Options: {
          ShowInactive: false,
        },
      },
    };

    return this.http
      .post<Octopus.AppointmentFieldOptionsResponse>(
        `${this.context.api}${Octopus.Action.READ}`,
        request,
      )
      .pipe(
        map((result) => {
          if (result.Code === Octopus.ResponseStatusCode.Successful) {
            const mapper = new AppointmentFieldOptionMapper();
            return mapper.from(result);
          }
          return { categories: [], results: [], locations: [] };
        }),
      );
  }

  create(
    appointmentForm: AppointmentForm,
    onProgress?: SeriesSaveProgressCallback,
  ): Observable<string | null> {
    const mapper = new AppointmentFormMapper(toUtcInstant);
    const recurrence = appointmentForm.recurrence;
    if (!recurrence) {
      return this.postCreate(mapper.from(appointmentForm));
    }

    return this.createSeries(
      expandRecurrenceDates(recurrence).map((day) =>
        mapper.from(shiftAppointmentFormToDay(appointmentForm, day)),
      ),
      recurrence,
      onProgress,
    );
  }

  createFromCalendarEvent(
    event: OutlookCalendarEvent,
    attendees: CalendarAttendee[],
    currentUserKey: string,
    locationOptions: ListItem<string>[],
    onProgress?: SeriesSaveProgressCallback,
  ): Observable<string | null> {
    const location = resolveAppointmentLocation(
      event.location,
      locationOptions,
    );
    const toCreate = (occurrence: OutlookCalendarEvent) =>
      mapCalendarEventToAppointmentCreate(
        occurrence,
        attendees,
        currentUserKey,
        location,
        toUtcInstant,
      );

    if (!event.recurrence) {
      return this.postCreate(toCreate(event));
    }

    return this.createSeries(
      expandRecurrenceDates(event.recurrence).map((day) =>
        toCreate(shiftCalendarEventToDay(event, day)),
      ),
      event.recurrence,
      onProgress,
    );
  }

  private createSeries(
    occurrences: Octopus.AppointmentCreate[],
    recurrence: AppointmentRecurrence,
    onProgress?: SeriesSaveProgressCallback,
  ): Observable<string | null> {
    const [first, ...rest] = occurrences;
    if (!first) {
      return throwError(
        () => new Error('Recurring appointment has no occurrences'),
      );
    }
    if (occurrences.length > MAX_RECURRENCE_OCCURRENCES) {
      return throwError(
        () =>
          new AppointmentOccurrenceLimitError(
            occurrences.length,
            MAX_RECURRENCE_OCCURRENCES,
          ),
      );
    }

    const pattern = mapRecurrenceToRecurringPattern(recurrence);

    return this.postCreateData({ ...first, RecurringPattern: pattern }).pipe(
      tap(() =>
        onProgress?.({ savedCount: 1, totalCount: occurrences.length }),
      ),
      switchMap((created) =>
        this.saveSeriesOccurrences(
          created,
          rest,
          pattern,
          occurrences.length,
          onProgress,
        ),
      ),
    );
  }

  private saveSeriesOccurrences(
    created: Octopus.AppointmentCreate,
    rest: Octopus.AppointmentCreate[],
    pattern: Octopus.AppointmentRecurringPattern,
    occurrenceCount: number,
    onProgress?: SeriesSaveProgressCallback,
  ): Observable<string | null> {
    let savedCount = 1;
    const trackSavedChunk = (chunkLength: number) => {
      savedCount += chunkLength;
      onProgress?.({ savedCount, totalCount: occurrenceCount });
    };

    return this.resolveRecurringPatternKey(created).pipe(
      switchMap((patternKey) =>
        this.saveOccurrenceBatches(rest, pattern, patternKey, trackSavedChunk),
      ),
      map(() => created.Key),
      catchError((error) =>
        throwError(
          () =>
            new AppointmentSeriesError(
              created.Key ?? '',
              { savedCount, totalCount: occurrenceCount },
              error,
            ),
        ),
      ),
    );
  }

  private saveOccurrenceBatches(
    occurrences: Octopus.AppointmentCreate[],
    pattern: Octopus.AppointmentRecurringPattern,
    patternKey: string,
    onChunkSaved: (chunkLength: number) => void,
  ): Observable<void[]> {
    return from(
      occurrences.map((occurrence) => ({
        ...occurrence,
        RecurringPattern: { ...pattern, key: patternKey },
      })),
    ).pipe(
      bufferCount(OCTOPUS_BATCH_SIZE),
      concatMap((chunk) => this.saveOccurrenceChunk(chunk, onChunkSaved)),
      toArray(),
    );
  }

  private saveOccurrenceChunk(
    chunk: Octopus.AppointmentCreate[],
    onChunkSaved: (chunkLength: number) => void,
  ): Observable<void> {
    return this.postCreateBatchChunk(chunk).pipe(
      tap(() => onChunkSaved(chunk.length)),
    );
  }

  private resolveRecurringPatternKey(
    created: Octopus.AppointmentCreate,
  ): Observable<string> {
    const echoedKey = created.RecurringPattern?.key;
    if (echoedKey) return of(echoedKey);
    if (!created.Key) {
      return throwError(
        () => new Error('Recurring appointment created without a key'),
      );
    }

    return this.readRecurringPatternKey(created.Key).pipe(
      switchMap((patternKey) =>
        patternKey
          ? of(patternKey)
          : throwError(
              () => new Error('Recurring pattern key could not be resolved'),
            ),
      ),
    );
  }

  private readRecurringPatternKey(
    appointmentKey: string,
  ): Observable<string | null> {
    const request: Octopus.AppointmentReadRequest = {
      Compatibility: APPOINTMENT_SCHEMA_COMPATIBILITY,
      Configuration: Octopus.AppointmentReadDriver,
      Appointment: {
        Scope: {
          Fields: {
            Key: 1,
            RecurringPattern: { key: 1 },
          },
        },
        Criteria: {
          SearchQuery: { Key: { $EQ: appointmentKey } },
        },
      },
      Globalization: Octopus.DefaultGlobalization,
    };

    return this.http
      .post<Octopus.AppointmentResponse>(
        `${this.context.api}${Octopus.Action.READ}`,
        request,
      )
      .pipe(
        map((result) =>
          result?.Code === Octopus.ResponseStatusCode.Successful
            ? (result.Appointment?.Data?.[0]?.RecurringPattern?.key ?? null)
            : null,
        ),
      );
  }

  private postCreateBatchChunk(
    items: Octopus.AppointmentCreate[],
  ): Observable<void> {
    const request: Octopus.AppointmentBatchCreateRequest = {
      Compatibility: APPOINTMENT_SCHEMA_COMPATIBILITY,
      Globalization: Octopus.DefaultGlobalization,
      ...Object.fromEntries(
        items.map((data, index) => [batchItemKey(index), { Data: data }]),
      ),
    };

    return this.http
      .post<Octopus.AppointmentBatchCreateResponse>(
        `${this.context.api}${Octopus.Action.CREATE}`,
        request,
        { context: skipTimeoutRetry() },
      )
      .pipe(
        map((result) => {
          const allCreated =
            result?.Code === Octopus.ResponseStatusCode.Successful &&
            items.every((_, index) => !!result[batchItemKey(index)]?.Data?.Key);
          if (!allCreated) {
            throw new HttpErrorResponse({
              error: 'Unsuccessful batch response',
            });
          }
        }),
      );
  }

  private postCreate(
    data: Octopus.AppointmentCreate,
  ): Observable<string | null> {
    return this.postCreateData(data).pipe(map((created) => created.Key));
  }

  private postCreateData(
    data: Octopus.AppointmentCreate,
  ): Observable<Octopus.AppointmentCreate> {
    const request: Octopus.AppointmentCreateRequest = {
      Compatibility: APPOINTMENT_SCHEMA_COMPATIBILITY,
      Appointment: {
        Data: data,
      },
      Globalization: Octopus.DefaultGlobalization,
    };
    return this.http
      .post<Octopus.AppointmentCreateResponse>(
        `${this.context.api}${Octopus.Action.CREATE}`,
        request,
        { context: skipTimeoutRetry() },
      )
      .pipe(
        map((result) => {
          if (result?.Code === Octopus.ResponseStatusCode.Successful) {
            return result.Appointment.Data;
          }
          throw new HttpErrorResponse({ error: 'Unsuccessful response' });
        }),
      );
  }

  findMatchingAppointment(
    event: OutlookCalendarEventSummary,
    currentUserKey: string,
  ): Observable<Octopus.Appointment | null> {
    const request = {
      Configuration: Octopus.AppointmentReadDriver,
      Appointment: {
        Scope: {
          Fields: {
            Key: 1,
            Subject: 1,
            StartDate: 1,
            EndDate: 1,
          },
        },
        Criteria: {
          SearchQuery: this.getMatchingAppointmentQuery(event, currentUserKey),
        },
      },
      Globalization: Octopus.DefaultGlobalization,
    };

    return this.http
      .post<Octopus.AppointmentResponse>(
        `${this.context.api}${Octopus.Action.READ}`,
        request,
      )
      .pipe(
        map((result) => {
          if (result?.Code !== Octopus.ResponseStatusCode.Successful) {
            throw new HttpErrorResponse({ error: 'Unsuccessful response' });
          }
          return result.Appointment?.Data?.[0] ?? null;
        }),
      );
  }

  private getMatchingAppointmentQuery(
    event: OutlookCalendarEventSummary,
    currentUserKey: string,
  ): Octopus.LogicalQuery<Octopus.Appointment> {
    const { StartDate, EndDate } = mapCalendarEventToAppointmentDates(
      event,
      toUtcInstant,
    );
    const query: Octopus.LogicalQuery<Octopus.Appointment> = {
      $AND: [
        { 'Users/Item/Key': { $IN: [currentUserKey] } },
        { Subject: { $EQ: event.subject } },
      ],
    };

    if (StartDate === EndDate) {
      query.$AND?.push({ StartDate: { $RANGE: [StartDate, EndDate] } });
    } else {
      query.$AND?.push(
        { StartDate: { $EQ: StartDate } },
        { EndDate: { $EQ: EndDate } },
      );
    }

    return query;
  }

  getSummary(key: string, type: string): Observable<AppointmentSummary> {
    const query = this.getSummaryQuery(key, type);
    const fields = this.getSummaryScope();
    const request = {
      Compatibility: {
        AppointmentObject: '1.0',
      },
      Appointment: {
        Scope: {
          Fields: fields,
        },
        Criteria: {
          SearchQuery: query,
          Top: 1,
        },
        OrderBy: {
          Fields: [{ StartDate: 'ASC' }],
        },
      },
      'Appointment.Count': {
        Scope: {
          Fields: {
            '$COUNT()': 1,
          },
        },
        Criteria: {
          SearchQuery: query,
        },
      },

      Globalization: Octopus.DefaultGlobalization,
    };

    return this.http
      .post<Octopus.AppointmentSummaryResponse>(
        `${this.context.api}${Octopus.Action.READ}`,
        request,
      )
      .pipe(
        map((result) => {
          if (result.Code === Octopus.ResponseStatusCode.Successful) {
            const mapper = new AppointmentSummaryMapper();
            return mapper.from(result);
          }

          return {
            appointmentAmount: 0,
          };
        }),
      );
  }

  private getSummaryQuery(
    key: string,
    type: string,
  ): Octopus.LogicalQuery<Octopus.Appointment> {
    const query: Octopus.LogicalQuery<Octopus.Appointment> = {
      $AND: [{ StartDate: { $GT: new Date().toISOString() } }],
    };

    if (type == 'lead') {
      query.$AND?.push({ 'Leads/Key': { $EQ: key } });
    }
    if (type == 'abentry') {
      query.$AND?.push({ 'AbEntries/Key': { $EQ: key } });
    }

    return query;
  }

  private getSummaryScope(): Octopus.Scope<Octopus.Appointment> {
    return {
      Key: 1,
      Subject: 1,
      StartDate: 1,
      EndDate: 1,
      DisplayValue: 1,
      Location: {
        DisplayValue: 1,
      },
      CreationDate: 1,
      Users: [
        {
          DisplayValue: 1,
        },
      ],
      AbEntries: [
        {
          FirstName: 1,
          LastName: 1,
          Type: 1,
          CompanyName: 1,
        },
      ],
      Leads: [
        {
          FirstName: 1,
          LastName: 1,
        },
      ],
    };
  }
}
